// ArtefactResultBody — renders the concrete content of one produced agent
// artefact in the run-detail "Result" section.
//
// The server composes `AgentTask.artefacts[]` by dereferencing each
// `job.producedArtefactIds` entry to its concrete Object Type row (camelCase
// properties; PRD #261 follow-up). This component renders that row. Rather than
// 20 bespoke per-type components, it is field-driven: it surfaces the content
// fields the artefact taxonomy shares (a headline via `summariseArtefact`, the
// long-text / list / variant / citation fields, then key metadata chips), and
// falls back to a structured field list so no artefact type ever renders blank.
// `client-communication-draft` (EMISSARY email) gets a tailored email layout.

import type { AgentArtefactView } from '@septima/contract';
import { Mail } from 'lucide-react';
import { cn } from '@/lib/utils';
import { summariseArtefact } from '@/lib/use-review-queue';
import EmailDraftActions from '@/components/email/EmailDraftActions';

// ---- row field helpers (the row is a loose camelCase record) ----------------

type Row = Record<string, unknown>;

function str(row: Row, key: string): string | null {
    const v = row[key];
    return typeof v === 'string' && v.trim() ? v.trim() : null;
}

function strArray(row: Row, key: string): string[] | null {
    const v = row[key];
    if (!Array.isArray(v)) return null;
    const out = v.filter((x): x is string => typeof x === 'string' && x.trim().length > 0);
    return out.length > 0 ? out : null;
}

function scalar(row: Row, key: string): string | null {
    const v = row[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
    if (typeof v === 'boolean') return v ? 'yes' : 'no';
    return null;
}

interface VariantView {
    label: string;
    body: string;
}

function variants(row: Row): VariantView[] | null {
    const v = row.variants;
    if (!Array.isArray(v)) return null;
    const out: VariantView[] = [];
    for (const raw of v) {
        if (!raw || typeof raw !== 'object') continue;
        const o = raw as Record<string, unknown>;
        const body = typeof o.body === 'string' ? o.body.trim() : '';
        if (!body) continue;
        const label =
            (typeof o.kind === 'string' && o.kind) ||
            (typeof o.tone === 'string' && o.tone) ||
            'variant';
        out.push({ label, body });
    }
    return out.length > 0 ? out : null;
}

// The long-text fields the taxonomy uses for its primary prose, in priority
// order. Rendered as labelled paragraphs (skipping the one already used as the
// headline so it is not repeated).
const TEXT_FIELDS: Array<{ key: string; label: string }> = [
    { key: 'text', label: 'Fact' },
    { key: 'body', label: 'Body' },
    { key: 'narrativeDraft', label: 'Narrative' },
    { key: 'narrativeParagraph', label: 'Narrative' },
    { key: 'performanceSummary', label: 'Performance' },
    { key: 'driftDetails', label: 'Drift detail' },
    { key: 'classificationRationale', label: 'Rationale' },
    { key: 'rationale', label: 'Rationale' },
    { key: 'feeDisclosure', label: 'Fee disclosure' },
    { key: 'detail', label: 'Detail' },
    { key: 'description', label: 'Description' },
    { key: 'recommendedAction', label: 'Recommended action' },
    { key: 'queryContext', label: 'Context' },
    { key: 'counterfactualAnchor', label: 'What would change this' },
];

// String-array fields rendered as bullet lists.
const LIST_FIELDS: Array<{ key: string; label: string }> = [
    { key: 'recommendations', label: 'Recommendations' },
    { key: 'riskWarnings', label: 'Risk warnings' },
    { key: 'actionsRequired', label: 'Actions required' },
    { key: 'deltaFindings', label: 'Changes' },
    { key: 'staleMarkers', label: 'Stale markers' },
    { key: 'tags', label: 'Tags' },
];

// Short scalar metadata rendered as labelled chips.
const META_FIELDS: Array<{ key: string; label: string }> = [
    { key: 'category', label: 'category' },
    { key: 'intent', label: 'intent' },
    { key: 'confidence', label: 'confidence' },
    { key: 'severity', label: 'severity' },
    { key: 'status', label: 'status' },
    { key: 'verdict', label: 'verdict' },
    { key: 'scope', label: 'scope' },
    { key: 'mandate', label: 'mandate' },
    { key: 'reviewPeriod', label: 'period' },
    { key: 'period', label: 'period' },
    { key: 'owner', label: 'owner' },
    { key: 'dueDescription', label: 'due' },
    { key: 'daysUntilDue', label: 'days until due' },
];

// Noise fields excluded from the generic fallback field list.
const FALLBACK_SKIP = new Set<string>([
    'id',
    'producedBy',
    'previousVersion',
    'traceRef',
    'reviewStatus',
    'reviewedBy',
    'reviewedAt',
    'rejectionReason',
    'revisionReason',
    'clientId',
    'callId',
    'sourceLineId',
    'selectedVariantIndex',
    'materialisedLetterId',
    'redlineVsPrior',
    'brief',
    'sourceInventory',
    'compositionPlan',
    'handoffChain',
    'citations',
    'variants',
]);

// ---- presentational primitives ----------------------------------------------

function Para({ label, text }: { label: string; text: string }) {
    return (
        <div className="flex flex-col gap-0.5">
            <span className="text-annotation font-medium text-[rgb(var(--color-text-tertiary))]">
                {label}
            </span>
            <p className="text-annotation text-[rgb(var(--color-text-secondary))] leading-snug whitespace-pre-wrap">
                {text}
            </p>
        </div>
    );
}

function BulletList({ label, items }: { label: string; items: string[] }) {
    return (
        <div className="flex flex-col gap-1">
            <span className="text-annotation font-medium text-[rgb(var(--color-text-tertiary))]">
                {label}
            </span>
            <ul className="flex flex-col gap-1 list-disc pl-4">
                {items.map((item, i) => (
                    <li
                        key={i}
                        className="text-annotation text-[rgb(var(--color-text-secondary))] leading-snug"
                    >
                        {item}
                    </li>
                ))}
            </ul>
        </div>
    );
}

function MetaChips({ chips }: { chips: Array<{ label: string; value: string }> }) {
    return (
        <div className="flex items-center gap-1.5 flex-wrap">
            {chips.map((c) => (
                <span
                    key={c.label}
                    className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-[rgb(var(--color-surface-secondary))] text-[rgb(var(--color-text-tertiary))]"
                >
                    <span className="opacity-70">{c.label}</span>
                    <span className="tabular-nums text-[rgb(var(--color-text-secondary))]">
                        {c.value}
                    </span>
                </span>
            ))}
        </div>
    );
}

function VariantsPreview({ items }: { items: VariantView[] }) {
    return (
        <div className="flex flex-col gap-1.5">
            <span className="text-annotation font-medium text-[rgb(var(--color-text-tertiary))]">
                Variants
            </span>
            {items.map((v, i) => (
                <div
                    key={i}
                    className="rounded-md border border-[rgb(var(--color-divider))] px-2.5 py-2 bg-[rgb(var(--color-surface-secondary)/0.4)]"
                >
                    <span className="text-[10px] font-mono uppercase tracking-wide text-[rgb(var(--color-text-tertiary))]">
                        {v.label}
                    </span>
                    <p className="text-annotation text-[rgb(var(--color-text-secondary))] leading-snug whitespace-pre-wrap line-clamp-6 mt-0.5">
                        {v.body}
                    </p>
                </div>
            ))}
        </div>
    );
}

// ---- per-type tailored layouts ----------------------------------------------

// EMISSARY email draft — subject + recipient + selected variant body.
function EmailBody({ row }: { row: Row }) {
    const subject = str(row, 'subject');
    const recipientName = str(row, 'recipientName');
    const recipientEmail = str(row, 'recipientEmail');
    const vs = variants(row);
    const selected = typeof row.selectedVariantIndex === 'number' ? row.selectedVariantIndex : 0;
    const body = vs ? (vs[selected]?.body ?? vs[0]?.body ?? '') : '';
    const recipient = [recipientName, recipientEmail].filter(Boolean).join(' · ');
    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-1 rounded-md border border-[rgb(var(--color-divider))] px-2.5 py-2 bg-[rgb(var(--color-surface-secondary)/0.4)]">
                <div className="text-card-title text-[rgb(var(--color-text-primary))] flex items-center gap-1.5">
                    <Mail className="w-3 h-3" strokeWidth={2} />
                    Draft email
                </div>
                {recipient && (
                    <div className="text-annotation text-[rgb(var(--color-text-secondary))] truncate">
                        <span className="text-[rgb(var(--color-text-tertiary))]">To:</span>{' '}
                        {recipient}
                    </div>
                )}
                {subject && (
                    <div className="text-body-medium text-[rgb(var(--color-text-primary))]">
                        {subject}
                    </div>
                )}
                {body && (
                    <p className="text-annotation text-[rgb(var(--color-text-secondary))] leading-snug whitespace-pre-wrap">
                        {body}
                    </p>
                )}
            </div>
            {body && (
                <div className="flex">
                    <EmailDraftActions to={recipientEmail} subject={subject ?? ''} body={body} />
                </div>
            )}
        </div>
    );
}

// Onboarding pack — list the assembled documents.
function packDocuments(row: Row): string[] | null {
    const docs = row.documents;
    if (!Array.isArray(docs)) return null;
    const out: string[] = [];
    for (const raw of docs) {
        if (typeof raw === 'string') {
            out.push(raw);
        } else if (raw && typeof raw === 'object') {
            const o = raw as Record<string, unknown>;
            const title =
                (typeof o.title === 'string' && o.title) ||
                (typeof o.kind === 'string' && o.kind) ||
                (typeof o.name === 'string' && o.name) ||
                null;
            if (title) out.push(title);
        }
    }
    return out.length > 0 ? out : null;
}

// ---- the field-driven renderer ----------------------------------------------

function GenericArtefactBody({ row, headline }: { row: Row; headline: string | null }) {
    const usedTextKeys = new Set<string>();

    const paras: Array<{ label: string; text: string }> = [];
    for (const { key, label } of TEXT_FIELDS) {
        const value = str(row, key);
        if (!value) continue;
        // Skip the field that already produced the headline so it isn't repeated.
        if (headline && value === headline) {
            usedTextKeys.add(key);
            continue;
        }
        usedTextKeys.add(key);
        paras.push({ label, text: value });
    }

    const lists: Array<{ label: string; items: string[] }> = [];
    for (const { key, label } of LIST_FIELDS) {
        const items = strArray(row, key);
        if (items) lists.push({ label, items });
    }

    const docs = packDocuments(row);
    const vs = variants(row);
    const sourceQuote = str(row, 'sourceQuote');
    const sourceTimestamp = str(row, 'sourceTimestamp');

    const chips: Array<{ label: string; value: string }> = [];
    for (const { key, label } of META_FIELDS) {
        const value = scalar(row, key);
        if (value) chips.push({ label, value });
    }

    const renderedAnything =
        paras.length > 0 ||
        lists.length > 0 ||
        !!docs ||
        !!vs ||
        !!sourceQuote ||
        chips.length > 0 ||
        !!headline;

    // Structured fallback: any remaining string / string[] scalar not already
    // surfaced and not noise, so an unmapped type is still legible.
    const fallback: Array<{ label: string; text: string }> = [];
    if (!renderedAnything) {
        for (const [key, value] of Object.entries(row)) {
            if (FALLBACK_SKIP.has(key) || usedTextKeys.has(key)) continue;
            if (typeof value === 'string' && value.trim()) {
                fallback.push({ label: key, text: value.trim() });
            } else if (Array.isArray(value)) {
                const items = value.filter((x): x is string => typeof x === 'string');
                if (items.length > 0) fallback.push({ label: key, text: items.join(', ') });
            } else if (typeof value === 'number' && Number.isFinite(value)) {
                fallback.push({ label: key, text: String(value) });
            }
        }
    }

    return (
        <div className="flex flex-col gap-2.5">
            {headline && (
                <p className="text-body-medium text-[rgb(var(--color-text-primary))] leading-snug whitespace-pre-wrap">
                    {headline}
                </p>
            )}
            {chips.length > 0 && <MetaChips chips={chips} />}
            {sourceQuote && (
                <blockquote className="border-l-2 border-[rgb(var(--color-divider))] pl-2.5 text-annotation italic text-[rgb(var(--color-text-secondary))]">
                    &ldquo;{sourceQuote}&rdquo;
                    {sourceTimestamp && (
                        <span className="not-italic font-mono text-[rgb(var(--color-text-tertiary))]">
                            {' '}
                            · {sourceTimestamp}
                        </span>
                    )}
                </blockquote>
            )}
            {paras.map((p) => (
                <Para key={p.label + p.text.slice(0, 8)} label={p.label} text={p.text} />
            ))}
            {lists.map((l) => (
                <BulletList key={l.label} label={l.label} items={l.items} />
            ))}
            {docs && <BulletList label="Documents" items={docs} />}
            {vs && <VariantsPreview items={vs} />}
            {fallback.map((f) => (
                <Para key={f.label} label={f.label} text={f.text} />
            ))}
            {!renderedAnything && fallback.length === 0 && (
                <p className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                    Artefact recorded with no displayable content.
                </p>
            )}
        </div>
    );
}

export default function ArtefactResultBody({
    artefact,
    className,
}: {
    artefact: AgentArtefactView;
    className?: string;
}) {
    const row = artefact.row;
    const body =
        artefact.artefactType === 'client-communication-draft' ? (
            <EmailBody row={row} />
        ) : (
            <GenericArtefactBody row={row} headline={summariseArtefact(row)} />
        );
    return <div className={cn('flex flex-col gap-2', className)}>{body}</div>;
}
